import { Component, OnInit, Type, ViewChild } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IssuesService } from 'src/app/_services/issues.service';
import { VehiclesService } from 'src/app/_services/vehicles.service';
import { ActiveIssueFormComponent } from '../active-issue-form/active-issue-form.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NoteDialogComponent } from '../note-dialog/note-dialog.component';
import { SignoffIssueComponent } from '../signoff-issue/signoff-issue.component';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { ActiveIssueReportDialogComponent } from '../active-issue-report-dialog/active-issue-report-dialog.component';
import { Table } from 'primeng/table';
import { CreateServiceReminderComponent } from '../../service-reminder/create-service-reminder/create-service-reminder.component';
import { ArchivesService } from 'src/app/_services/archives.service';

@Component({
  selector: 'app-active-issues-list',
  templateUrl: './active-issues-list.component.html',
  styleUrls: ['./active-issues-list.component.scss'],
})
export class ActiveIssuesListComponent implements OnInit {
  @ViewChild('dt') dt: Table | undefined;

  ref: DynamicDialogRef | undefined;
  totalRecords = 0;
  issueData: any[] = [];
  currentFilter = '';
  currentPage: number = 0;

  loadingSignOff$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  selectedDescription: any[] = [];
  activeIssueSearch = '';

  columns = [
    { field: 'vehicle_reference', header: 'Vehicle Reference' },
    { field: 'description', header: 'Description' },
  ];

  constructor(
    public dialogService: DialogService,
    public vehiclesService: VehiclesService,
    public issuesService: IssuesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private archivesService: ArchivesService
  ) {}

  ngOnInit(): void {
    this.loadActiveIssues({ first: 0, rows: 10 });
  }

  // ---------------------------- Data loading ---------------------------- //

  loadActiveIssues(event: any) {
    this.issuesService.getActiveIssues(this.currentFilter).subscribe({
      next: (data: any) => {
        this.issueData = this.transformData(data.body?.records || []);
        this.totalRecords = data.body?.total_records || 0;
        this.loadingSignOff$.next(false);
      },
      error: () => {
        this.loadingSignOff$.next(false);
      },
    });
  }

  transformData(issues: any): any[] {
    const mergedIssues: any[] = [];

    issues.forEach((issue: any) => {
      const existingIssue = mergedIssues.find(
        (i) => i.vehicle_reference === issue.vehicle_reference
      );
      if (!issue.signed_off) {
        if (existingIssue) {
          existingIssue.descriptions.push({
            id: issue.id,
            description: issue.description,
            vehicle_reference: issue.vehicle_reference,
            remarks: issue.remarks,
            vehicle_id: issue.vehicle_id,
          });
        } else {
          mergedIssues.push({
            ...issue,
            descriptions: [
              {
                id: issue.id,
                description: issue.description,
                vehicle_reference: issue.vehicle_reference,
                remarks: issue.remarks,
                vehicle_id: issue.vehicle_id,
              },
            ],
          });
        }
      }
    });
    return mergedIssues;
  }

  // ---------------------------- Search ---------------------------- //

  filterGlobal(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.activeIssueSearch = inputElement.value.trim();
    this.currentFilter = this.activeIssueSearch;
    this.loadActiveIssues({ first: 0, rows: 5 });
  }

  clearActiveIssueSearch() {
    this.activeIssueSearch = '';
    this.currentFilter = '';
    this.loadActiveIssues({ first: 0, rows: 5 });
  }

  // ---------------------------- Selection ---------------------------- //

  clearActiveIssueSelection() {
    this.selectedDescription = [];
  }

  get selectedVehicleReference(): string {
    return (
      this.selectedDescription[0]?.vehicle_reference || 'selected vehicle'
    );
  }

  get selectedVehicleKey(): string | number | null {
    return this.selectedDescription.length
      ? this.getVehicleSelectionKey(this.selectedDescription[0])
      : null;
  }

  onSelectedDescriptionsChange(selection: any[]): void {
    const nextSelection = Array.isArray(selection) ? selection : [];

    if (nextSelection.length <= 1) {
      this.selectedDescription = nextSelection;
      return;
    }

    const validSelection = this.getSingleVehicleSelection(nextSelection);

    if (validSelection.length !== nextSelection.length) {
      this.showSingleVehicleSelectionWarning();
    }

    this.selectedDescription = validSelection;
  }

  isDescriptionSelectionDisabled(description: any): boolean {
    const selectedVehicleKey = this.selectedVehicleKey;

    if (
      selectedVehicleKey === null ||
      this.isDescriptionSelected(description)
    ) {
      return false;
    }

    return this.getVehicleSelectionKey(description) !== selectedVehicleKey;
  }

  private getVehicleSelectionKey(description: any): string | number | null {
    return description?.vehicle_id ?? description?.vehicle_reference ?? null;
  }

  private isDescriptionSelected(description: any): boolean {
    return this.selectedDescription.some(
      (selected) => selected.id === description.id
    );
  }

  private isSingleVehicleSelection(selection: any[]): boolean {
    return (
      this.getSingleVehicleSelection(selection).length === selection.length
    );
  }

  private getSingleVehicleSelection(selection: any[]): any[] {
    if (!selection.length) {
      return [];
    }

    const selectedVehicleKey = this.getVehicleSelectionKey(selection[0]);

    return selection.filter(
      (description) =>
        this.getVehicleSelectionKey(description) === selectedVehicleKey
    );
  }

  private showSingleVehicleSelectionWarning(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Selection limited',
      detail: 'Select issues from one vehicle at a time to sign off.',
      key: 'active_issues',
    });
  }

  // ---------------------------- Row actions ---------------------------- //

  handleCellClick(event: Event): void {
    event.stopPropagation();
  }

  openSignOffFromDescription(
    event: Event,
    activeIssue: any,
    description: any
  ): void {
    this.handleCellClick(event);

    if (this.isDescriptionSelectionDisabled(description)) {
      this.showSingleVehicleSelectionWarning();
      return;
    }

    this.showSignOffModal(activeIssue, description);
  }

  deleteDescription(description: any): void {
    this.issuesService.deleteActiveIssue(description.id).subscribe(
      () => {
        this.loadActiveIssues({ first: this.currentPage, rows: 5 });
        this.messageService.add({
          severity: 'info',
          summary: 'Confirmed',
          detail: 'Record deleted',
          key: 'active_issues',
        });
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete record',
          key: 'active_issues',
        });
      }
    );
  }

  deleteMultipleDescriptions(): void {
    const toDelete = [...this.selectedDescription];
    this.selectedDescription = [];
    if (!toDelete.length) {
      return;
    }

    forkJoin(
      toDelete.map((description) =>
        this.issuesService.deleteActiveIssue(description.id)
      )
    ).subscribe({
      next: () => {
        this.reloadActiveIssuesList();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${toDelete.length} record(s) deleted`,
          key: 'active_issues',
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete one or more records',
          key: 'active_issues',
        });
      },
    });
  }

  confirmDelete(description: any = null) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this issue?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',

      accept: () => {
        this.deleteDescription(description);
      },
    });
  }

  confirmMultiDelete() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete these issues?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',

      accept: () => {
        this.deleteMultipleDescriptions();
      },
    });
  }

  // ---------------------------- Reports ---------------------------- //

  generate_excel_report() {
    this.issuesService.getIssueReport();
  }

  generate_csv_report() {
    this.issuesService.getCsvReport();
  }

  // ---------------------------- Dialogs ---------------------------- //

  showSignOffModal(activeIssue: any, description: any) {
    if (
      Array.isArray(description) &&
      !this.isSingleVehicleSelection(description)
    ) {
      this.selectedDescription = this.getSingleVehicleSelection(description);
      this.showSingleVehicleSelectionWarning();
      return;
    }

    this.openDialog(SignoffIssueComponent, {
      data: {
        data: activeIssue,
        descriptionData: description,
        selectedDescription: description,
      },
      header: 'Sign Off Issue',
      onClose: (result) => {
        this.resetSelectionAndArchiveForm();
        if (result) {
          this.reloadActiveIssuesList();
        }
      },
    });
  }

  showCreateServiceReminder() {
    this.openDialog(CreateServiceReminderComponent, {
      header: 'Create Service Reminder',
      onClose: (result) => {
        if (result) {
          this.reloadActiveIssuesList();
        }
      },
    });
  }

  showAddActiveIssueModal() {
    this.openDialog(ActiveIssueFormComponent, {
      data: {
        formdata: null,
        listOfVehicles: this.vehiclesService.getVehicles(),
      },
      header: 'Create a new Active Issue',
      onClose: (result) => {
        if (result) {
          this.reloadActiveIssuesList();
        }
      },
    });
  }

  showEditActiveIssueModal(activeIssue: any) {
    const formdata = Array.isArray(activeIssue) ? activeIssue : [activeIssue];

    this.openDialog(ActiveIssueFormComponent, {
      data: {
        formdata,
        listOfVehicles: this.vehiclesService.getVehicles(),
      },
      header: 'Edit Active Issue',
      onClose: (result) => {
        this.resetSelectionAndArchiveForm();
        if (result) {
          this.reloadActiveIssuesList();
        }
      },
    });
  }

  showNotesDialog(rowData: any) {
    this.openDialog(NoteDialogComponent, {
      data: {
        formdata: rowData,
        listOfVehicles: this.vehiclesService.getVehicles(),
      },
      header: 'Notes',
    });
  }

  showReportDialog() {
    this.openDialog(ActiveIssueReportDialogComponent, {
      header: 'Reporting',
      width: '60%',
      styleClass: '',
    });
  }

  private openDialog(
    component: Type<any>,
    config: {
      header: string;
      data?: any;
      width?: string;
      styleClass?: string;
      onClose?: (result: any) => void;
    }
  ): void {
    this.ref = this.dialogService.open(component, {
      data: config.data,
      header: config.header,
      width: config.width ?? '75%',
      styleClass: config.styleClass ?? 'update-record-dialog-shell',
      closeOnEscape: true,
    });

    if (config.onClose) {
      this.ref.onClose.subscribe(config.onClose);
    }
  }

  private reloadActiveIssuesList(): void {
    this.loadActiveIssues({ first: this.currentPage, rows: 5 });
  }

  private resetSelectionAndArchiveForm(): void {
    this.clearActiveIssueSelection();
    this.archivesService.archiveForm.reset();
  }
}
